import React from 'react';
import { Users, Calendar, Zap, Globe } from 'lucide-react';

const HomePage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">B2B Matchmaking</h1>
              <p className="text-gray-600">by Mobivent</p>
            </div>
            <img 
              src="https://mobivent.fr/logo.png" 
              alt="Mobivent" 
              className="h-12"
              onError={(e) => e.target.style.display = 'none'}
            />
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center">
          <h2 className="text-5xl font-bold text-gray-900 mb-6">
            B2B Matchmaking Platform
            <br />
            <span className="text-blue-600">for Professional Events</span>
          </h2>
          <p className="text-xl text-gray-600 mb-12 max-w-3xl mx-auto">
            Connect with the right partners at your events.
            Our intelligent algorithm analyzes your needs and suggests the best matches.
          </p>
          
          <div className="flex justify-center gap-4">
            <a 
              href="#events" 
              className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition shadow-lg"
            >
              Discover our events
            </a>
            <a 
              href="#contact" 
              className="bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition border-2 border-blue-600"
            >
              Contact us
            </a>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-4 py-20">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          <div className="bg-white p-8 rounded-xl shadow-md text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="text-blue-600" size={32} />
            </div>
            <h3 className="text-xl font-bold mb-3">Smart Matching</h3>
            <p className="text-gray-600">
              Algorithm based on your offers and requests for relevant matches
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md text-center">
            <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="text-purple-600" size={32} />
            </div>
            <h3 className="text-xl font-bold mb-3">Automatic Scheduling</h3>
            <p className="text-gray-600">
              Automatic assignment of time slots and meeting tables
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Zap className="text-green-600" size={32} />
            </div>
            <h3 className="text-xl font-bold mb-3">Simple & Fast</h3>
            <p className="text-gray-600">
              Sign up in minutes, instant matching
            </p>
          </div>

          <div className="bg-white p-8 rounded-xl shadow-md text-center">
            <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Globe className="text-orange-600" size={32} />
            </div>
            <h3 className="text-xl font-bold mb-3">Multilingual</h3>
            <p className="text-gray-600">
              Interface available in French, English and German
            </p>
          </div>
        </div>
      </section>

      {/* Events Section */}
      <section id="events" className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">Available Events</h2>
          <p className="text-xl text-gray-600">
            Select your event to access the matchmaking platform
          </p>
        </div>

        <div className="bg-white rounded-xl shadow-lg p-12 text-center">
          <Calendar className="mx-auto mb-6 text-blue-600" size={64} />
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            Access to events is provided by direct link
          </h3>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Each event has its own dedicated access URL. 
            Check your invitation email or contact the event organizer to get your personalized link.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-blue-600 text-white py-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to optimize your networking?</h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of professionals who trust our platform
          </p>
          <a 
            href="#contact" 
            className="inline-block bg-white text-blue-600 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition shadow-lg"
          >
            Contact us
          </a>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="max-w-7xl mx-auto px-4 py-20">
        <div className="text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Contact Us</h2>
          <p className="text-xl text-gray-600 mb-8">
            Have a question? Our team is here to help
          </p>
          <div className="flex justify-center gap-8">
            <div>
              <p className="text-gray-600 mb-2">Email</p>
              <a href="mailto:contact@mobivent.fr" className="text-blue-600 font-semibold hover:underline">
                contact@mobivent.fr
              </a>
            </div>
            <div>
              <p className="text-gray-600 mb-2">Phone</p>
              <a href="tel:+33123456789" className="text-blue-600 font-semibold hover:underline">
                +33 1 23 45 67 89
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="mb-4">© 2025 Mobivent - All rights reserved</p>
          <div className="flex justify-center gap-6">
            <a href="#" className="hover:text-blue-400 transition">Legal Notice</a>
            <a href="#" className="hover:text-blue-400 transition">Privacy</a>
            <a href="#" className="hover:text-blue-400 transition">Terms</a>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default HomePage;